import { formatDayPartsForMessage } from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/is-entry-out-of-active-day-parts';
import { t } from 'state/intl/translations';
import type { IDayPartBoundary } from 'state/menu/main-menu/types';

type OfferWithDayParts = {
  daypart?: readonly (string | null | undefined)[] | null;
};

/**
 * Daypart keys are free-form CMS strings shared by two documents (the offer and
 * the restaurant's menu config), so they drift in casing and spacing. Comparing
 * them normalized only ever widens the match, and the failure this guards is a
 * false "unavailable" on an offer the guest could have redeemed.
 */
const normalizeDayPartKey = (key: string) => key.trim().toLowerCase();

/**
 * Names the dayparts a deeplinked offer is limited to, when the restaurant is
 * outside all of them.
 *
 * A daypart-only offer is not store-restricted, so the loyalty service still
 * returns it and nothing else marks it unavailable. Without this the guest
 * lands on an offer detail modal they cannot redeem and is told only that it is
 * unavailable, with no way to act on it.
 *
 * This repeats the offer branch of `checkIncentiveAvailableForDayPart` rather
 * than importing it: that module pulls in the in-restaurant-redemption and
 * Sanity GraphQL graphs, which is far too much weight for every offer lookup.
 *
 * Every uncertain case resolves to "available", because a wrong message hides a
 * redeemable offer behind recovery chrome. That is why an unresolved daypart
 * source produces no message. A resolved empty schedule is not uncertain: no
 * configured daypart covers now, so a tagged offer is unavailable. Burgers For
 * Breakfast is not special cased: the legacy check gates that daypart behind a
 * flag this lookup deliberately does not reach for, so an offer tagged for an
 * active Burgers For Breakfast daypart is simply treated as available.
 */
export function resolveOfferDayPartUnavailableMessage({
  offer,
  activeDayParts,
  isDayPartsSourceResolved,
}: {
  offer: OfferWithDayParts | undefined;
  activeDayParts: readonly IDayPartBoundary[];
  isDayPartsSourceResolved: boolean;
}): string | undefined {
  const offerDayParts = (offer?.daypart ?? []).filter((part): part is string => Boolean(part));

  if (!offerDayParts.length || !isDayPartsSourceResolved) {
    return undefined;
  }

  const offerDayPartKeys = new Set(offerDayParts.map(normalizeDayPartKey));

  return activeDayParts.some(dayPart =>
    offerDayPartKeys.has(normalizeDayPartKey(dayPart.key ?? ''))
  )
    ? undefined
    : t('offerOnlyAvailableOnDayParts', {
        dayParts: formatDayPartsForMessage(offerDayParts),
        defaultValue: 'Offer only available on {dayParts}',
      });
}
